import {ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree} from "@angular/router";
import {Inject, Injectable, PLATFORM_ID} from "@angular/core";
import { Constants } from "./constants";
import { environment } from "./environments/environment";
import {UserService} from "./services/user/user.service";
import {isPlatformBrowser, isPlatformServer} from "@angular/common";
import {UtilsService} from "./services/utils";

@Injectable()
export class AuthGuard {
    constructor(
        public router: Router,
        @Inject(PLATFORM_ID) private platformId: any,
        private utils: UtilsService
    ) {
    }

    canActivate(route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot): boolean | UrlTree {

        if ( isPlatformBrowser(this.platformId) ) {
            if ( localStorage.getItem(Constants.LOCAL_TOKEN) && localStorage.getItem("app") == environment.appName ) {
                const forcePaymentPage = this.mustGoToCompanyPaymentRequired(state?.url);
                if (forcePaymentPage) return forcePaymentPage;
                console.log("Token ok. Allow navigation");
                return true;
            } else {
                console.log("Token NO ok. Deny navigation");
                this.router.navigate(["/login"]);
                return false;

            }
        }

        return true;

    }

    /**
     * For company managers with payment/subscription issues, force navigation to dedicated pages.
     * Priority: no subscription -> plan selection; payment issue -> payment-issue page.
     * "No subscription" takes precedence so users without a plan aren't shown "payment failed".
     */
    private mustGoToCompanyPaymentRequired(currentUrl?: string): UrlTree | null {
        if (this.utils.getJwtUserGroup() !== 'companymanager') {
            return null;
        }
        const status = this.utils.getJwtCompanyStatus();
        const hasPaymentIssue = status === 'paymentFailed' || status === 'pendingPayment';
        const hasActiveSubscription = this.utils.hasActiveSubscription();

        if (!hasActiveSubscription) {
            const paymentRequiredPath = '/companies/payment-required';
            if (currentUrl === paymentRequiredPath || currentUrl?.startsWith('/companies/payment-required')) {
                return null;
            }
            return this.router.createUrlTree(['/companies', 'payment-required']);
        }

        if (hasPaymentIssue) {
            const paymentIssuePath = '/companies/payment-issue';
            if (currentUrl === paymentIssuePath || currentUrl?.startsWith('/companies/payment-issue')) {
                return null;
            }
            return this.router.createUrlTree(['/companies', 'payment-issue']);
        }

        return null;
    }


    hasThisPermission(permissionToCheck: string): UrlTree | boolean {
        const listOfPermissions = this.utils.getJwtPermissions();

        if (listOfPermissions.length === 0) return this.router.createUrlTree(['/login']);
        if (!listOfPermissions.includes(permissionToCheck)) {
            return this.router.createUrlTree(['/login']);
        }
        return true;
    }

    /**
     * For company managers: when companyStatus is paymentFailed or pendingPayment, or when they have
     * no active subscription, allow only navigation to companies/payment-required.
     */
    canAccessCompanyArea(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        if (!isPlatformBrowser(this.platformId)) {
            return true;
        }
        return this.mustGoToCompanyPaymentRequired(state.url) ?? true;
    }

    /** Redirect shop-only company managers away from dashboard to I tuoi Ordini. */
    canAccessCompanyDashboard(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        const areaCheck = this.canAccessCompanyArea(route, state);
        if (areaCheck !== true) {
            return areaCheck;
        }
        if (this.utils.getJwtUserGroup() !== 'companymanager') {
            return true;
        }
        if (!this.utils.canAccessJwtService('dashboard') && this.utils.canAccessJwtService('onlineshop')) {
            return this.router.createUrlTree(['/companies/shop/orders']);
        }
        return true;
    }
}
